/**
 * Entwurf eines Textes mit verzögertem Speichern (ohne React, siehe useDraft). Ein Stand gilt erst als gespeichert, wenn der Server
 * ihn bestätigt hat: Scheitert das Speichern, bleibt der Entwurf ungespeichert, wird nicht vom Stand des Servers überschrieben und
 * nach einer Pause erneut gesendet. Optional:
 * - `backupKey`: der ungespeicherte Stand liegt zusätzlich im Browser (übersteht Schließen, Absturz, Abmelden) und kommt beim
 *   nächsten Öffnen zurück;
 * - `guard`: Ändert sich der Text auf dem Server, während hier Ungespeichertes liegt (anderes Fenster, anderes Gerät), wird nichts
 *   still überschrieben – `conflict` meldet es, gespeichert wird erst nach `keepMine` bzw. verworfen mit `discard`.
 */

export type DraftStatus = 'saved' | 'pending' | 'saving' | 'failed';

/** Speichern: `base` ist der Stand des Servers, auf dem der Entwurf aufbaut. Ein Promise, das mit `null` endet oder scheitert, gilt als fehlgeschlagen. */
export type DraftSave = (value: string, base: string) => unknown;

export interface DraftOptions {
  delayMs?: number;
  backupKey?: string;
  guard?: boolean;
  /** Zugriff auf den Browser-Speicher (Tests); Standard: localStorage, falls vorhanden */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

interface Backup {
  text: string;
  base: string;
}

/** Wartezeit vor dem nächsten Versuch nach einem Fehler: 2, 4, 8 … höchstens 60 Sekunden */
export const retryDelay = (attempt: number): number => Math.min(60_000, 2000 * 2 ** Math.max(0, attempt - 1));

const defaultStorage = (): DraftOptions['storage'] => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

const isThenable = (x: unknown): x is PromiseLike<unknown> => typeof (x as { then?: unknown } | null)?.then === 'function';

/** Alle Entwürfe mit Ungespeichertem (Warnung beim Schließen, Speichern beim Verlassen der Seite) */
const open = new Set<DraftSaver>();
export const unsavedDrafts = (): DraftSaver[] => [...open].filter((d) => d.unsaved);

export class DraftSaver {
  draft: string;
  /** Stand des Servers, auf dem der Entwurf aufbaut (zuletzt bestätigt bzw. übernommen) */
  base: string;
  /** zuletzt bekannter Stand des Servers */
  server: string;
  status: DraftStatus = 'saved';
  /** beim Öffnen aus dem Browser-Speicher zurückgeholt */
  restored = false;
  private dirty = false;
  private saving = false;
  private again = false;
  /** zuletzt gesendeter Stand: kommt er als (vorab eingetragener) Stand des Servers zurück, ist das keine fremde Änderung */
  private sent: string | null = null;
  private attempts = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private backupTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly delayMs: number;
  private readonly storage: DraftOptions['storage'];
  private listener: (() => void) | null = null;

  constructor(
    value: string,
    private save: DraftSave,
    private readonly opts: DraftOptions = {},
  ) {
    this.delayMs = opts.delayMs ?? 900;
    this.storage = opts.storage === undefined ? defaultStorage() : opts.storage;
    this.draft = value;
    this.base = value;
    this.server = value;
    const backup = this.readBackup();
    if (backup && backup.text !== value) {
      this.draft = backup.text;
      this.base = backup.base;
      this.dirty = true;
      this.status = 'pending';
      this.restored = true;
      open.add(this);
      // der Server steht noch, wo der Entwurf begann: gleich nachholen; sonst entscheidet der Autor (conflict)
      if (!this.conflict) this.schedule(this.delayMs);
    } else if (backup) this.removeBackup();
  }

  /** Ungespeichertes liegt vor (auch während des Speicherns) */
  get unsaved(): boolean {
    return this.dirty || this.saving;
  }

  /** Der Server hat einen anderen Stand als den, auf dem der ungespeicherte Entwurf aufbaut (nur mit `guard`) */
  get conflict(): boolean {
    return !!this.opts.guard && this.dirty && !this.saving && this.server !== this.base;
  }

  /** Benachrichtigung bei jeder Änderung von Entwurf oder Status */
  subscribe(fn: (() => void) | null): void {
    this.listener = fn;
  }

  setSave(save: DraftSave): void {
    this.save = save;
  }

  /** Neuer Stand vom Server; überschreibt den Entwurf nur, wenn hier nichts Ungespeichertes liegt. */
  setValue(value: string): void {
    if (value === this.server || value === this.sent) return;
    this.server = value;
    if (!this.dirty && !this.saving) {
      this.draft = value;
      this.base = value;
    }
    this.emit();
  }

  change(value: string): void {
    if (value === this.draft) return;
    this.draft = value;
    this.dirty = true;
    open.add(this);
    if (this.status !== 'failed') this.status = 'pending';
    this.attempts = 0;
    this.schedule(this.delayMs);
    this.scheduleBackup();
    this.emit();
  }

  /** Sofort speichern (Verlassen des Feldes, Strg+S, Seite wird verborgen) */
  flush(): void {
    this.clearTimer();
    if (!this.dirty) return;
    if (this.saving) {
      this.again = true;
      return;
    }
    if (this.conflict) return;
    const value = this.draft;
    if (value === this.base) {
      this.settle();
      return;
    }
    this.writeBackup();
    this.dirty = false;
    this.saving = true;
    this.sent = value;
    this.status = 'saving';
    this.emit();
    let result: unknown;
    try {
      result = this.save(value, this.base);
    } catch {
      this.failed();
      return;
    }
    if (!isThenable(result)) {
      this.succeeded(value);
      return;
    }
    Promise.resolve(result).then(
      (r) => (r === null ? this.failed() : this.succeeded(value)),
      () => this.failed(),
    );
  }

  /** Konflikt: den eigenen Stand über den des Servers speichern */
  keepMine(): void {
    this.base = this.server;
    this.flush();
  }

  /** Konflikt bzw. Fehler: den eigenen Stand verwerfen und den des Servers übernehmen */
  discard(): void {
    this.clearTimer();
    this.dirty = false;
    this.draft = this.server;
    this.base = this.server;
    this.sent = null;
    this.attempts = 0;
    if (!this.saving) this.settle();
    this.emit();
  }

  /** Komponente wird entfernt: Ungespeichertes noch senden (der Entwurf bleibt bis zur Bestätigung in der Liste offener Entwürfe) */
  dispose(): void {
    this.listener = null;
    this.flush();
    if (!this.unsaved) open.delete(this);
  }

  private succeeded(value: string): void {
    this.saving = false;
    this.base = value;
    this.server = value;
    this.sent = null;
    this.attempts = 0;
    if (this.again) {
      this.again = false;
      this.dirty = this.dirty || this.draft !== value;
    }
    if (this.dirty) {
      this.status = 'pending';
      if (!this.timer) this.schedule(this.delayMs);
    } else this.settle();
    this.emit();
  }

  private failed(): void {
    this.saving = false;
    this.again = false;
    this.dirty = true;
    this.status = 'failed';
    this.attempts += 1;
    this.writeBackup();
    this.schedule(retryDelay(this.attempts));
    this.emit();
  }

  private settle(): void {
    this.dirty = false;
    this.status = 'saved';
    this.removeBackup();
    open.delete(this);
  }

  private schedule(ms: number): void {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, ms);
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private emit(): void {
    this.listener?.();
  }

  private readBackup(): Backup | null {
    if (!this.opts.backupKey || !this.storage) return null;
    try {
      const raw = this.storage.getItem(this.opts.backupKey);
      const b = raw ? (JSON.parse(raw) as Partial<Backup>) : null;
      return b && typeof b.text === 'string' && typeof b.base === 'string' ? { text: b.text, base: b.base } : null;
    } catch {
      return null;
    }
  }

  // der Browser-Speicher wird beim Tippen höchstens alle 400 ms beschrieben, vor jedem Senden und nach jedem Fehler sofort
  private scheduleBackup(): void {
    if (!this.opts.backupKey || !this.storage || this.backupTimer) return;
    this.backupTimer = setTimeout(() => {
      this.backupTimer = null;
      if (this.dirty) this.writeBackup();
    }, 400);
  }

  private writeBackup(): void {
    if (!this.opts.backupKey || !this.storage) return;
    try {
      this.storage.setItem(this.opts.backupKey, JSON.stringify({ text: this.draft, base: this.base } satisfies Backup));
    } catch {
      // Speicher voll oder gesperrt: dann eben ohne Sicherung im Browser
    }
  }

  private removeBackup(): void {
    if (this.backupTimer) clearTimeout(this.backupTimer);
    this.backupTimer = null;
    if (!this.opts.backupKey || !this.storage) return;
    try {
      this.storage.removeItem(this.opts.backupKey);
    } catch {
      // ohne Speicher nichts zu entfernen
    }
  }
}
